import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { LoadingOverlay } from "@mantine/core";
import { useFeatureAccess } from "../../context/FeatureAccessContext";
import OnboardingDashboard from "./components/OnboardingDashboard";
import { OnboardingProgressResponse } from "./types/onboarding";

/**
 * Onboarding page. No automatic redirect when all tasks are complete — if the user
 * navigates directly to /onboarding, they see the success message and content.
 * See docs/components/ONBOARDING_DASHBOARD_ARCHITECTURE.md for full behavior.
 *
 * Now uses /access endpoint via FeatureAccessContext for faster initial render.
 * The dashboard can render immediately once access data is available.
 */
export default function OnboardingPage() {
  const navigate = useNavigate();
  const { access, isLoading, refreshAccess } = useFeatureAccess();

  // Derive progress from access data (already fetched by FeatureAccessContext)
  const progress: OnboardingProgressResponse | null = useMemo(() => {
    if (!access?.onboarding) return null;

    return {
      initialOnboarding: access.onboarding.initial_onboarding,
      progressiveEmployees: access.onboarding.progressive_employees,
      progressiveServices: access.onboarding.progressive_services,
      progressivePayments: access.onboarding.progressive_payments,
      // Include stage if available (for backwards compatibility)
      initialOnboardingStage: access.onboarding.initial_onboarding_stage,
    } as OnboardingProgressResponse;
  }, [access]);

  // Redirect logic based on access data
  useEffect(() => {
    // Wait for access to load
    if (isLoading) return;

    // If no access data at all, redirect to account creation
    if (!access) {
      navigate("/account-creation");
      return;
    }

    // Check if initial onboarding is complete
    const initialOnboardingComplete =
      access.onboarding?.initial_onboarding === "complete";

    if (!initialOnboardingComplete) {
      // Initial onboarding not complete - redirect to account creation
      navigate("/account-creation");
    }
    // If complete, stay on this page and render the dashboard
  }, [access, isLoading, navigate]);

  const handleRefresh = async () => {
    try {
      await refreshAccess();
    } catch (error) {
      // Silently handle refresh errors
    }
  };

  // Show full-screen spinner only when loading access data initially
  if (isLoading) {
    return <LoadingOverlay visible />;
  }

  // If no progress (access not loaded or initial onboarding incomplete), show nothing
  // The useEffect will handle the redirect
  if (!progress || access?.onboarding?.initial_onboarding !== "complete") {
    return null;
  }

  return <OnboardingDashboard progress={progress} onRefresh={handleRefresh} />;
}
